// Fetch-based SSE keeps credentials in Authorization headers, never in the URL.
export async function readSystemLogStream(response, onEvent) {
  if (!response.ok) {
    const error = new Error(response.status === 401 || response.status === 403
      ? 'Access denied. Sign in with an active superadmin account.'
      : `Log stream unavailable (${response.status}).`);
    error.status = response.status;
    throw error;
  }
  if (!response.headers.get('content-type')?.includes('text/event-stream') || !response.body) {
    throw new Error('The server did not return a log stream.');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // The server bounds replay size; also guard against malformed proxy responses.
      if (buffer.length > 4 * 1024 * 1024) throw new Error('Log stream frame too large.');
      let boundary;
      while ((boundary = buffer.search(/\r?\n\r?\n/)) !== -1) {
        const frame = buffer.slice(0, boundary);
        const separator = buffer.slice(boundary).match(/^\r?\n\r?\n/)[0];
        buffer = buffer.slice(boundary + separator.length);
        let name = 'message';
        const data = [];
        for (const line of frame.split(/\r?\n/)) {
          if (line.startsWith('event:')) name = line.slice(6).trim();
          if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
        }
        if (data.length) onEvent(name, JSON.parse(data.join('\n')));
      }
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

export function mergeSystemLogs(current, incoming, limit = 1000) {
  const seen = new Set(current.map(entry => entry.id));
  return [...current, ...incoming.filter(entry => {
    if (seen.has(entry.id)) return false;
    seen.add(entry.id);
    return true;
  })].slice(-limit);
}

export function filterSystemLogs(entries, { level = '', source = '', search = '' }) {
  const query = search.trim().toLowerCase();
  return entries.filter(entry => (!level || entry.level === level)
    && (!source || entry.source === source)
    && (!query || [entry.message, entry.requestId, entry.timestamp, entry.deploymentId, entry.appName, entry.owner,
      entry.deploymentType, entry.phase, entry.job, entry.jobId, entry.action, entry.actor, entry.target].filter(Boolean).join(' ').toLowerCase().includes(query)));
}

export function formatSystemLog(entry) {
  const context = ['requestId', 'deploymentId', 'appName', 'owner', 'deploymentType', 'phase', 'job', 'jobId', 'action', 'actor', 'target']
    .filter(key => entry[key]).map(key => `[${key}=${entry[key]}]`).join(' ');
  return `${entry.timestamp} [${entry.level.toUpperCase()}] [${entry.source}]${context ? ` ${context}` : ''} ${entry.message}`;
}
